<script setup lang="ts">
  import { computed } from 'vue';
  import { storeToRefs } from 'pinia';
  import certificateIcon from '@/shared/assets/icons/CertificateIcon.svg';
  import { GaugeChart, type GaugeChartSegment } from '@/shared/ui';
  import { useDashboardStore } from '../../model/dashboard.store';
  import { formatNumber, formatVariationPct } from '../../model/format';
  import { formatPeriodLabel } from '../../model/period';
  import VariationBadge from '../VariationBadge.vue';
  import WidgetCard from '../WidgetCard.vue';

  const dashboardStore = useDashboardStore();
  const { data, isLoading, comparisonLabel, granularity, period } = storeToRefs(dashboardStore);

  const periodLabel = computed(() => formatPeriodLabel(granularity.value, period.value));

  const total = computed(() => data.value?.trainingStatus.total ?? 0);

  const segments = computed<GaugeChartSegment[]>(() => [
    {
      label: 'Concluídos',
      value: data.value?.trainingStatus.completed ?? 0,
      color: 'var(--chart-2)',
    },
    {
      label: 'Pendentes',
      value: data.value?.trainingStatus.pending ?? 0,
      color: 'var(--chart-1)',
    },
  ]);

  const variationPct = computed(() =>
    total.value > 0 ? (data.value?.trainingStatus.variationPct ?? null) : null
  );

  const badgeText = computed(() => {
    if (variationPct.value === null || variationPct.value === 0) return undefined;
    const direction = variationPct.value > 0 ? 'mais' : 'menos';
    return `${formatVariationPct(variationPct.value)} ${direction} treinamentos concluídos`;
  });
</script>

<template>
  <WidgetCard
    title="Status dos Treinamentos"
    :icon="certificateIcon"
    :period-label="periodLabel"
    :loading="isLoading"
    :has-data="data !== null"
    skeleton="gauge"
  >
    <div
      data-testid="training-status-widget"
      class="flex h-full flex-col"
    >
      <div class="flex min-h-0 flex-1 items-center justify-center">
        <GaugeChart
          :segments="segments"
          :center-value="formatNumber(total)"
          center-label="Treinamentos"
        />
      </div>
      <VariationBadge
        class="self-center"
        :variation-pct="variationPct"
        :text="badgeText"
        :comparison-label="comparisonLabel"
      />
    </div>
  </WidgetCard>
</template>
